import { useEffect, useRef } from 'react';
import { useMapsLibrary } from '@vis.gl/react-google-maps';

// How long after a Places autocomplete selection we should suppress the
// Enter-to-geocode fallback. Without this, picking a suggestion via Enter
// would double-fire (place_changed + geocode), with both racing onLocationChange.
const PLACE_SELECTION_DEBOUNCE_MS = 250;

/**
 * Top filter bar — three inputs matching the legacy build's layout:
 *   ZIP / address (Google Places autocomplete)  |  Radius dropdown  |  Category dropdown
 *
 * The Places autocomplete narrows to US + Canada and reports the resolved
 * lat/lng up to App via onLocationChange.
 */
export default function FilterBar({
  categories,
  selectedCategoryName,
  onCategoryChange,
  radius,
  onRadiusChange,
  radiusOptions,
  onLocationChange,
}) {
  const inputRef = useRef(null);
  const places = useMapsLibrary('places');
  // google.maps.Geocoder is part of the core Maps API — it's NOT a separately
  // loadable library, so useMapsLibrary('geocoding') would return null and
  // skip the listener setup. Once `places` is loaded the core API is too,
  // so we can use window.google.maps.Geocoder directly.
  const lastPlaceSelectedAt = useRef(0);

  // Wire Google Places Autocomplete to the ZIP input
  useEffect(() => {
    if (!places || !inputRef.current) return;

    const autocomplete = new places.Autocomplete(inputRef.current, {
      // Restrict to US + Canada (matches the current Provider Finder's geography)
      componentRestrictions: { country: ['us', 'ca'] },
      // Cities + ZIPs only. The legacy 'geocode' type also matched street
      // addresses, so typing "22125" returned "22125 107th Ave Jamaica NY"
      // instead of the Occoquan VA ZIP. Catherine flagged this 2026-05-18.
      // '(regions)' covers locality + postal_code + admin_area + country.
      types: ['(regions)'],
      fields: ['geometry', 'formatted_address'],
    });

    const listener = autocomplete.addListener('place_changed', () => {
      const place = autocomplete.getPlace();
      if (place?.geometry?.location) {
        lastPlaceSelectedAt.current = Date.now();
        onLocationChange({
          lat: place.geometry.location.lat(),
          lng: place.geometry.location.lng(),
          label: place.formatted_address,
        });
      }
    });

    return () => listener.remove();
  }, [places, onLocationChange]);

  // Enter-to-submit fallback for ZIP codes that don't appear in the Places
  // autocomplete dropdown. Catherine flagged 2026-05-18: some legit ZIPs
  // (e.g. 22125 Occoquan VA) don't show as suggestions, so the user is stuck.
  // Mirroring the legacy build's behavior: typing a ZIP + pressing Enter
  // geocodes the raw value and treats it as a place selection.
  useEffect(() => {
    const inputEl = inputRef.current;
    if (!inputEl || !places) return; // places loaded → core Maps API loaded too

    const geocoder = new window.google.maps.Geocoder();

    const handleKeyDown = (e) => {
      if (e.key !== 'Enter') return;
      // If a place_changed event just fired (user picked from dropdown via
      // Enter), don't double-fire as a geocode.
      if (Date.now() - lastPlaceSelectedAt.current < PLACE_SELECTION_DEBOUNCE_MS) return;

      const value = inputEl.value.trim();
      if (!value) return;

      e.preventDefault();
      e.stopPropagation();

      geocoder.geocode(
        { address: value, componentRestrictions: { country: 'us' } },
        (results, status) => {
          if (status === 'OK' && results && results[0] && results[0].geometry?.location) {
            const loc = results[0].geometry.location;
            lastPlaceSelectedAt.current = Date.now();
            onLocationChange({
              lat: loc.lat(),
              lng: loc.lng(),
              label: results[0].formatted_address,
            });
            inputEl.value = results[0].formatted_address;
            inputEl.blur();
          }
        }
      );
    };

    // Use capture phase so this fires BEFORE Google's bubble-phase handler.
    // Google's Autocomplete swallows Enter if its dropdown is open, which
    // would otherwise prevent the geocoder fallback from running.
    inputEl.addEventListener('keydown', handleKeyDown, true);
    return () => inputEl.removeEventListener('keydown', handleKeyDown, true);
  }, [places, onLocationChange]);

  // Reset search when the user empties the input field.
  // Google Places Autocomplete only fires place_changed on SELECTION; deleting
  // the text (backspace / select-all-delete) leaves searchCenter stale in App,
  // so results stick around instead of returning to the empty state.
  useEffect(() => {
    const inputEl = inputRef.current;
    if (!inputEl) return;
    const handleInput = (e) => {
      if (e.target.value === '') {
        onLocationChange(null);
      }
    };
    inputEl.addEventListener('input', handleInput);
    return () => inputEl.removeEventListener('input', handleInput);
  }, [onLocationChange]);

  return (
    <div className="pf-filterbar">
      <input
        ref={inputRef}
        type="text"
        inputMode="text"
        className="pf-filterbar__zip"
        placeholder="City/Zip Code"
        aria-label="Search by city or ZIP code"
        autoComplete="postal-code"
        spellCheck="false"
        autoCorrect="off"
        autoCapitalize="off"
        // Opt out of browser-extension injection (Grammarly etc.) — they inject
        // icons inline into text inputs which break the visible value.
        data-gramm="false"
        data-gramm_editor="false"
        data-enable-grammarly="false"
        data-lt-tmp-id="ignore"
      />

      <select
        className="pf-filterbar__radius"
        value={radius}
        onChange={e => onRadiusChange(Number(e.target.value))}
        aria-label="Search radius"
      >
        {radiusOptions.map(opt => (
          <option key={opt} value={opt}>
            {opt} mi
          </option>
        ))}
      </select>

      <select
        className="pf-filterbar__category"
        value={selectedCategoryName ?? ''}
        onChange={e => onCategoryChange(e.target.value || null)}
        aria-label="Select a device or treatment"
      >
        <option value="">Select a device…</option>
        {categories.map(c => (
          <option key={c.name} value={c.name}>
            {c.name}
          </option>
        ))}
      </select>
    </div>
  );
}
